import { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from "@mui/material";
import { useAuthApi } from "../hooks/useAuthApi";
import { MGMT } from "../env";
import type { Vendor } from "../types";
import { useToast } from "../toast";

interface Props {
  open: boolean;
  vendor: Vendor | null;
  onClose: () => void;
  onSaved: () => void;
}

export default function VendorDialog({ open, vendor, onClose, onSaved }: Props) {
  const api = useAuthApi();
  const [name, setName] = useState("");
  const [labelKey, setLabelKey] = useState("");
  const [labelValue, setLabelValue] = useState("");
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (!open) return;
    setName(vendor?.name ?? "");
    setLabelKey(vendor?.label?.key ?? "name");
    setLabelValue(vendor?.label?.value ?? vendor?.name ?? "");
  }, [open, vendor]);

  const save = async () => {
    const normalizedName = name.trim();
    const normalizedLabelKey = labelKey.trim();
    const normalizedLabelValue = labelValue.trim();
    if (!normalizedName) return;
    setSaving(true);
    try {
      const payload: { name: string; label?: { key: string; value: string } } = { name: normalizedName };
      if (normalizedLabelKey && normalizedLabelValue) {
        payload.label = { key: normalizedLabelKey, value: normalizedLabelValue };
      }
      if (vendor) {
        await api(`${MGMT}/vendors/${vendor.id}`, {
          method: "PATCH",
          body: JSON.stringify(payload),
        });
        toast.show("Vendor updated");
      } else {
        await api(`${MGMT}/vendors`, {
          method: "POST",
          body: JSON.stringify(payload),
        });
        toast.show("Vendor created");
      }
      onSaved();
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{vendor ? "Edit Vendor" : "Add Vendor"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Vendor Name"
            required
            value={name}
            placeholder="e.g. ABB, Wallbox, Schneider"
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            label="Label Key"
            value={labelKey}
            placeholder="e.g. brand, family"
            onChange={(e) => setLabelKey(e.target.value)}
          />
          <TextField
            label="Label Value"
            value={labelValue}
            placeholder="e.g. ABB Chargers"
            onChange={(e) => setLabelValue(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={save} variant="contained" disabled={saving || !name.trim()}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
